import { ButtonType, DialogFooterButtonsWidget, DialogPositioningBehavior, DialogType, TextAreaWidget } from '@tableau/widgets';
import React from 'react';

import { Formatters, Messages } from '~src/codegen/Localize';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';

import { IParsedPublishToSalesforceResponse } from '../restApi/requests/PublishToSalesforce';
import { PartialPublishDialogStyle } from '../styles/PartialPublishDialogStyle';
import { PublishErrorIcon, PublishSuccessIcon } from './PublishIcons';

export const PartialPublishDialogTestIds = {
  Dialog: 'partial-publish-dialog',
  FailureList: 'partial-publish-dialog-failure-list',
  FooterButtons: 'partial-publish-dialog-footer-buttons',
  CloseButton: 'close-button',
  GoToSalesforceButton: 'go-to-salesforce-button',
};

export interface PartialPublishDialogProps {
  parsedResponse: IParsedPublishToSalesforceResponse;
  close: () => void;
}

/**
 * This dialog is displayed when Publish to Salesforce is partially successful.
 * It provides users with the list of views that failed to publish.
 */
export class PartialPublishDialog extends React.Component<PartialPublishDialogProps> {
  getDialogHeader = () => {
    const header: string = Messages.publishToSalesforcePartialPublishDialogHeader({
      ITEM_COUNT: this.props.parsedResponse.numFailures
    });
    return <div style={PartialPublishDialogStyle.HeaderStyle}>{header}</div>;
  };

  getResultSummary = () => {
    const successString: string = Messages.publishToSalesforcePartialSuccess({
      ITEM_COUNT: this.props.parsedResponse.numSuccesses,
      ITEM_COUNT_LOC: Formatters.formatCount(this.props.parsedResponse.numSuccesses),
      APP_NAME: this.props.parsedResponse.appName
    });
    const failureString: string = Messages.publishToSalesforcePartialFailure({
      ITEM_COUNT: this.props.parsedResponse.numFailures,
      ITEM_COUNT_LOC: Formatters.formatCount(this.props.parsedResponse.numFailures)
    });

    return <div>
      <p>
        <img src={PublishSuccessIcon.medium} alt={Messages.publishToSalesforceSuccessIconAltText()} style={PartialPublishDialogStyle.IconStyle} />
        {successString}
      </p>
      <p>
        <img src={PublishErrorIcon.medium} alt={Messages.publishToSalesforceErrorIconAltText()} style={PartialPublishDialogStyle.IconStyle} />
        {failureString}
      </p>
    </div>;
  };

  getFailureList = () => {
    return <TextAreaWidget
      testId={PartialPublishDialogTestIds.FailureList}
      className={PartialPublishDialogStyle.FailureListStyle}
      label=''
      rows={3}
      style={PartialPublishDialogStyle.TextAreaStyle}
      value={this.props.parsedResponse.failureNames.join('\n')}
      readOnly={true} />;
  };

  getFooterButtons = () => {
    return <DialogFooterButtonsWidget
      testId={PartialPublishDialogTestIds.FooterButtons}
      buttons={[
        {
          buttonType: ButtonType.LowEmphasis,
          children: Messages.publishToSalesforceSuccessAction(),
          onClick: this.props.parsedResponse.successAction,
          testId: PartialPublishDialogTestIds.GoToSalesforceButton,
        },
        {
          buttonType: ButtonType.Primary,
          children: Messages.close(),
          onClick: this.props.close,
          testId: PartialPublishDialogTestIds.CloseButton,
        },
      ]} />;
  };

  public static open(parsedResponse: IParsedPublishToSalesforceResponse) {
    const closeDialog = () => {
      modalInstance && modalInstance.close();
    };
    const modalInstance = ReactModalService.open({
      title: 'Views Failed to Publish',
      testId: PartialPublishDialogTestIds.Dialog,
      type: DialogType.NormalWithoutTitleBar,
      dialogWidth: TabDialogWidth.width375px,
      showCloseButton: false,
      dismissDialogOnCloseAction: true,
      dialogStyle: PartialPublishDialogStyle.DialogStyle,
      positioningBehavior: DialogPositioningBehavior.Centered,
      dialogContent: <PartialPublishDialog parsedResponse={parsedResponse} close={closeDialog} />,
    });
    return modalInstance.result;
  }

  public render() {
    return <div>
      {this.getDialogHeader()}
      {this.getResultSummary()}
      {this.getFailureList()}
      {this.getFooterButtons()}
    </div>;
  }
}
